package diana.soleil.design.iraniancanadienbazzar.core.designsystem.component

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.material3.NavigationBar
import androidx.compose.material3.NavigationBarItem
import androidx.compose.material3.NavigationBarItemDefaults
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.shadow
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.theme.AppTheme

/**
 * A single bottom-nav destination. [iconName] is a Material Symbols ligature (e.g. "home",
 * "category", "dynamic_feed", "chat", "person") — copy from the Stitch design. [label] is localized.
 */
data class AppBottomNavItem(
    val label: String,
    val iconName: String,
    val route: String,
    /** Extra modifier on the rendered item — e.g. a feature-tour [tourAnchor]. */
    val modifier: Modifier = Modifier,
)

/**
 * Mobile bottom navigation. Active = teal icon+label; inactive = muted. The single bottom-nav
 * component.
 *
 * When [centerAction] is provided, a raised circular primary button floats over the bar's center
 * (the "Post" pattern): items are split evenly around a reserved middle slot that carries the
 * [centerActionLabel] under the raised circle. Callers pass localized copy; icon-only otherwise.
 */
@Composable
fun AppBottomNavBar(
    items: List<AppBottomNavItem>,
    selectedRoute: String,
    onSelect: (AppBottomNavItem) -> Unit,
    modifier: Modifier = Modifier,
    centerAction: (() -> Unit)? = null,
    centerActionIcon: String = "add",
    centerActionLabel: String? = null,
    /** Extra modifier on the raised center action — e.g. a feature-tour [tourAnchor]. */
    centerActionModifier: Modifier = Modifier,
) {
    if (centerAction == null) {
        NavigationBar(
            modifier = modifier,
            containerColor = AppTheme.colors.surface,
            contentColor = AppTheme.colors.onSurface,
        ) {
            items.forEach { item -> BarItem(item, item.route == selectedRoute, onSelect) }
        }
        return
    }

    val half = (items.size + 1) / 2
    NavigationBar(
        modifier = modifier,
        containerColor = AppTheme.colors.surface,
        contentColor = AppTheme.colors.onSurface,
    ) {
        items.take(half).forEach { item -> BarItem(item, item.route == selectedRoute, onSelect) }
        // Middle slot renders the raised circle as its own icon (offset upward past the bar edge)
        // so the circle and its label stay aligned with the SLOT — an overlay at the bar's visual
        // center drifts whenever the item count is odd.
        NavigationBarItem(
            selected = false,
            onClick = centerAction,
            modifier = centerActionModifier,
            icon = {
                // Surface ring around the raised circle: it separates the button from whatever
                // content scrolls behind the bar edge (a bare primary circle over teal-ish cards
                // read muddled), and visually seats the float into the bar.
                Box(
                    modifier = Modifier
                        .offset(y = -AppTheme.spacing.md)
                        .size(AppTheme.sizes.navCenterAction)
                        .shadow(AppTheme.elevation.level2, AppTheme.shapes.pill)
                        .background(AppTheme.colors.surface, AppTheme.shapes.pill)
                        .padding(AppTheme.spacing.xs),
                    contentAlignment = Alignment.Center,
                ) {
                    Box(
                        modifier = Modifier
                            .fillMaxSize()
                            .background(AppTheme.colors.primary, AppTheme.shapes.pill),
                        contentAlignment = Alignment.Center,
                    ) {
                        AppIcon(
                            name = centerActionIcon,
                            size = AppTheme.sizes.iconMd,
                            tint = AppTheme.colors.onPrimary,
                            contentDescription = centerActionLabel,
                        )
                    }
                }
            },
            label = centerActionLabel?.let {
                { Text(it, style = AppTheme.typography.labelSm, maxLines = 1, overflow = TextOverflow.Ellipsis) }
            },
            alwaysShowLabel = true,
            colors = NavigationBarItemDefaults.colors(
                indicatorColor = AppTheme.colors.surface,
                unselectedIconColor = AppTheme.colors.onSurfaceVariant,
                unselectedTextColor = AppTheme.colors.onSurfaceVariant,
            ),
        )
        items.drop(half).forEach { item -> BarItem(item, item.route == selectedRoute, onSelect) }
    }
}

@Composable
private fun androidx.compose.foundation.layout.RowScope.BarItem(
    item: AppBottomNavItem,
    selected: Boolean,
    onSelect: (AppBottomNavItem) -> Unit,
) {
    NavigationBarItem(
        selected = selected,
        onClick = { onSelect(item) },
        modifier = item.modifier,
        // Stitch renders the active tab's icon with FILL=1.
        icon = { AppIcon(name = item.iconName, filled = selected, contentDescription = item.label) },
        // maxLines/ellipsis so a longer label ("Profile") never clips mid-glyph at the screen
        // edge when six slots (5 tabs + raised center) get tight on a narrow phone.
        label = { Text(item.label, style = AppTheme.typography.labelSm, maxLines = 1, overflow = TextOverflow.Ellipsis) },
        colors = NavigationBarItemDefaults.colors(
            selectedIconColor = AppTheme.colors.primary,
            selectedTextColor = AppTheme.colors.primary,
            indicatorColor = AppTheme.colors.categoryChip,
            unselectedIconColor = AppTheme.colors.onSurfaceVariant,
            unselectedTextColor = AppTheme.colors.onSurfaceVariant,
        ),
    )
}
